import { useMemo, useState } from "react";
import { Autocomplete, createFilterOptions, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CoreInterfaces, CorePropsInterfaces } from "src/core/Models";

function getContactLabel(contact: CoreInterfaces.ContactPersonItem): string {
  const name = `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim();
  return contact.email ? `${name} <${contact.email}>` : name;
}

/**
 * Picks a contact person registered in Upsales. It never holds a value itself:
 * the selection is handed to `onSelect` and the field is cleared for the next pick.
 */
export default function ClientContactPicker({
  contacts,
  isContactAdded,
  onSelect,
  isDisabled,
  testId,
}: CorePropsInterfaces.ClientContactPickerProps): JSX.Element | null {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");

  // Match on the name only, so e.g. "se" does not hit every "@se.gt.com" address.
  const filterOptions = useMemo(
    () =>
      createFilterOptions<CoreInterfaces.ContactPersonItem>({
        stringify: (contact) =>
          `${contact.firstName ?? ""} ${contact.lastName ?? ""}`,
      }),
    []
  );

  if (!contacts?.length) {
    return null;
  }

  return (
    <Autocomplete
      data-testid={testId}
      className="gt-Autocomplete gt-clientDetails__contact-picker"
      options={contacts}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value, reason) =>
        setInputValue(reason === "reset" ? "" : value)
      }
      onChange={(_, contact) => contact && onSelect(contact)}
      getOptionLabel={getContactLabel}
      getOptionKey={(contact) => contact.key}
      getOptionDisabled={isContactAdded}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      filterOptions={filterOptions}
      renderOption={(props, contact) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps}>
            {getContactLabel(contact)}
          </li>
        );
      }}
      noOptionsText={t("General.ClientDetails.NoMatchingUpsalesContact")}
      disabled={isDisabled}
      blurOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t("General.ClientDetails.SelectUpsalesContact")}
        />
      )}
    />
  );
}
